import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Box,
  Grid,
  GridItem,
  Divider,
  Text,
  Button,
  useClipboard,
} from '@chakra-ui/react';
import { config, tipConfig } from '../../config';

interface BuyMeACoffeeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const QR_SIZE = 100;
const QR_URL = (address: string) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${QR_SIZE}x${QR_SIZE}&data=${encodeURIComponent(`bitcoin:${address}`)}`;

/** Same-origin /public page so BMC’s script parses normally (see public/bmc-button.html). */
const BMC_IFRAME_SRC = `${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/bmc-button.html`;

export const BuyMeACoffeeModal: React.FC<BuyMeACoffeeModalProps> = ({ isOpen, onClose }) => {
  const { hasCopied, onCopy } = useClipboard(tipConfig.btcAddress);
  const bmc = tipConfig.buyMeACoffeeEmbed;
  const bmcScale = bmc.displayWidthPx / bmc.layoutWidthPx;
  const bmcClipH = Math.ceil(bmc.layoutHeightPx * bmcScale) + bmc.clipPaddingPx;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay />
      <ModalContent maxW="560px">
        <ModalHeader fontSize="md" pb={2}>
          Show your support with a tip
        </ModalHeader>
        <ModalBody pb={4}>
          <Grid
            w="100%"
            templateColumns={{ base: 'minmax(0,1fr)', sm: 'minmax(0,1fr) auto minmax(0,1fr)' }}
            columnGap={{ sm: 4 }}
            rowGap={{ base: 6 }}
            alignItems="stretch"
          >
            <GridItem
              minW={0}
              borderBottomWidth={{ base: '1px', sm: 0 }}
              borderBottomColor="gray.200"
              pb={{ base: 6, sm: 0 }}
            >
              <Box
                textAlign="center"
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                <Text fontSize="xs" fontWeight="600" mb={2} color="gray.600">
                  Bitcoin
                </Text>
                <Box
                  as="img"
                  src={QR_URL(tipConfig.btcAddress)}
                  alt="Bitcoin QR code"
                  width={`${QR_SIZE}px`}
                  height={`${QR_SIZE}px`}
                  mb={2}
                  borderRadius="md"
                />
                <Text
                  fontSize="xs"
                  fontFamily="mono"
                  wordBreak="break-all"
                  color={config.black}
                  mb={2}
                  w="100%"
                >
                  {tipConfig.btcAddress}
                </Text>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={onCopy}
                  colorScheme="gray"
                  _hover={{ bg: 'gray.50' }}
                >
                  {hasCopied ? 'Copied' : 'Copy address'}
                </Button>
              </Box>
            </GridItem>
            <GridItem
              display={{ base: 'none', sm: 'flex' }}
              alignItems="stretch"
              justifyContent="center"
              aria-hidden
            >
              <Divider orientation="vertical" borderColor="gray.200" />
            </GridItem>
            <GridItem minW={0}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                textAlign="center"
                h="100%"
              >
                <Text fontSize="xs" fontWeight="600" mb={3} color="gray.600">
                  Buy Me a Coffee
                </Text>
                <Box
                  flex="1"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  w="100%"
                  minH={0}
                >
                  {isOpen ? (
                    <Box
                      w={`${bmc.displayWidthPx}px`}
                      h={`${bmcClipH}px`}
                      position="relative"
                      overflow="hidden"
                      mt={-5}
                    >
                      <iframe
                        title="Buy me a coffee"
                        src={BMC_IFRAME_SRC}
                        scrolling="no"
                        style={{
                          position: 'absolute',
                          left: '50%',
                          top: 0,
                          width: bmc.layoutWidthPx,
                          height: bmc.layoutHeightPx,
                          border: 'none',
                          transform: `translateX(-50%) scale(${bmcScale})`,
                          transformOrigin: 'top center',
                        }}
                      />
                    </Box>
                  ) : null}
                </Box>
              </Box>
            </GridItem>
          </Grid>
          <Text
            fontSize="sm"
            color="gray.600"
            textAlign="center"
            mt={4}
            pt={3}
            borderTopWidth="1px"
            borderTopColor="gray.100"
          >
            Thank you for your support!
          </Text>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default BuyMeACoffeeModal;
